import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { cn } from '../../lib/cn';
import { logout } from '../../store/slices/auth';
import { InsightsSearch } from './InsightsSearch';
import { tourReplayed } from '../../store/slices/tour';
import { selectUser } from '../../store/selectors/auth';
import { userHasRole, adminHomeFor } from '../../lib/roles';
import { selectPresence } from '../../store/selectors/chat';
import { PanelSwitcher } from '../../components/PanelSwitcher';
import logoutIcon from '../../assets/insights/icons/logout.svg';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import arrowIcon from '../../assets/insights/icons/arrow-right-16.svg';
import { getAgentStatusAction, setAgentStatusAction } from '../../store/slices/chat';
import {
  DropdownMenu,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuSeparator,
} from '../../ui/DropdownMenu';

// Presence values from chat EAgentStatus; the design labels "online" as Active.
const STATUS_LABELS = { online: 'Active', away: 'Away', offline: 'Offline' };
const STATUS_DOT = {
  online: 'bg-[#69d86b]',
  away: 'bg-dq-orange',
  offline: 'bg-dq-500',
};

export const InsightsTopBar = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const user = useAppSelector(selectUser);
  const presence = useAppSelector(selectPresence);
  const adminHome = adminHomeFor(user);
  const status = presence.status;
  useEffect(() => {
    dispatch(getAgentStatusAction());
  }, [dispatch]);

  const handleLogout = () => {
    dispatch(logout());
    navigate('/login');
  };

  return (
    <header className="flex h-[55px] shrink-0 items-center justify-between gap-4 border-b border-[#1a242a] bg-dq-bg px-4">
      <PanelSwitcher label="Insight Summary" className="font-dq" />

      <div className="hidden min-w-0 flex-1 justify-center md:flex">
        <InsightsSearch />
      </div>
      <div data-tour="menu" className="flex shrink-0 items-center gap-3">
        <DropdownMenu>
          <DropdownMenuTrigger className="flex items-center gap-1 rounded-[30px] bg-dq-900 py-1.5 pl-2 pr-1.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-dq-700">
            <span className="flex items-center gap-1">
              <span
                className={cn('h-2 w-2 rounded-full', STATUS_DOT[status] || STATUS_DOT.offline)}
              />
              <span className="font-dq text-[11px] font-bold text-dq-title">
                You ·{' '}
                {presence.canSet === false
                  ? userHasRole(user, 'admin')
                    ? 'Admin'
                    : 'Unavailable'
                  : STATUS_LABELS[status] || 'Unknown'}
              </span>
            </span>
            <img src={arrowIcon} alt="" className="h-4 w-4 rotate-90" />
          </DropdownMenuTrigger>
          <DropdownMenuContent align="end" className="w-44 border-dq-900 bg-dq-950">
            {Object.keys(STATUS_LABELS).map((value) => (
              <DropdownMenuItem
                key={value}
                disabled={presence.pending || presence.canSet !== true}
                onClick={() => dispatch(setAgentStatusAction(value))}
                className="gap-2 font-dq text-[12px]"
              >
                <span className={cn('h-2 w-2 rounded-full', STATUS_DOT[value])} />
                {STATUS_LABELS[value]}
              </DropdownMenuItem>
            ))}
            {adminHome && (
              <>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  onClick={() => navigate(adminHome)}
                  className="font-dq text-[12px]"
                >
                  Back to admin
                </DropdownMenuItem>
              </>
            )}
            <DropdownMenuSeparator />
            <DropdownMenuItem
              onSelect={() => dispatch(tourReplayed())}
              className="font-dq text-[12px]"
            >
              Show the tour again
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
        {presence.error && (
          <span role="alert" className="text-[11px] text-dq-orange">
            {presence.error}
          </span>
        )}
        <button
          type="button"
          onClick={handleLogout}
          aria-label="Log out"
          className="-mx-2.5 flex h-11 w-11 items-center justify-center rounded-full focus:outline-none focus-visible:ring-2 focus-visible:ring-dq-700"
        >
          <span className="relative h-6 w-6 overflow-hidden rounded">
            <img
              src={logoutIcon}
              alt=""
              className="absolute -left-[7px] -top-[7px] h-[38px] w-[38px] max-w-none"
            />
          </span>
        </button>
      </div>
    </header>
  );
};
